import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Trash2, UserCircle } from "lucide-react";

export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchCustomers = async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await api.get(`/customers${params}`);
      setCustomers(res.data);
    } catch { toast.error("Failed to load customers"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCustomers(); }, []); // eslint-disable-line
  useEffect(() => {
    const timer = setTimeout(() => { if (search !== undefined) fetchCustomers(); }, 300);
    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Deleted"); fetchCustomers(); }
    catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>;

  return (
    <div className="p-8" data-testid="customers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="customers-title">Customers</h1>
          <p className="text-sm text-slate-500 mt-1">{customers.length} customers</p>
        </div>
        <Button onClick={() => navigate("/dashboard/customers/new")} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="add-customer-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Customer
        </Button>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder="Search by name, phone, email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="customers-search" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="customers-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Orders</TableHead>
              <TableHead className="text-right">Spent</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">No customers found.</TableCell></TableRow>
            ) : customers.map((c) => (
              <TableRow key={c.customer_id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/dashboard/customers/${c.customer_id}`)} data-testid={`customer-row-${c.customer_id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {c.photo_url ? (
                      <img src={c.photo_url} alt="" className="h-8 w-8 rounded-full object-cover border border-slate-200" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-[var(--brand-primary)] text-white flex items-center justify-center text-xs font-bold">
                        {(c.first_name?.[0] || "")}{(c.last_name?.[0] || "")}
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-slate-900">{c.first_name} {c.last_name}</p>
                      {c.company_name && <p className="text-xs text-slate-400">{c.company_name}</p>}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-slate-600">{c.phone || "-"}</TableCell>
                <TableCell className="text-slate-600">{c.email || "-"}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs capitalize">{c.customer_type}</Badge></TableCell>
                <TableCell className="text-right font-medium">{c.total_orders || 0}</TableCell>
                <TableCell className="text-right font-medium">${(c.total_spent || 0).toFixed(2)}</TableCell>
                <TableCell>
                  <Badge variant={c.is_active ? "default" : "secondary"} className="text-xs">{c.is_active ? "Active" : "Inactive"}</Badge>
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(c.customer_id)} data-testid={`del-customer-${c.customer_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
